import type { Group } from 'three';
import type { TweenSet } from './animation.js';

/** Bouncy elastic easeOut — nature's dopamine curve. */
const easeOutElastic = (k: number): number => {
  if (k <= 0) return 0;
  if (k >= 1) return 1;
  const c4 = (2 * Math.PI) / 3;
  return Math.pow(2, -10 * k) * Math.sin((k * 10 - 0.75) * c4) + 1;
};

const easeOutCubic = (k: number): number => 1 - Math.pow(1 - k, 3);

export const runBoardPulse = (group: Group, tweens: TweenSet, intensity = 1.18): void => {
  const dur = 0.9;
  let t = 0;
  const baseScale = group.scale.x;
  tweens.add({
    update(dt) {
      t += dt;
      const k = Math.min(1, t / dur);
      const e = easeOutElastic(k);
      const s = baseScale + (intensity - 1) * (1 - e);
      group.scale.set(s, s, s);
      return k < 1;
    },
  });
};

export const runBoardShake = (group: Group, tweens: TweenSet, magnitude = 0.18): void => {
  const dur = 0.4;
  let t = 0;
  const baseX = group.position.x;
  const baseY = group.position.y;
  tweens.add({
    update(dt) {
      t += dt;
      const k = Math.min(1, t / dur);
      const decay = 1 - k;
      group.position.x = baseX + (Math.random() - 0.5) * magnitude * decay;
      group.position.y = baseY + (Math.random() - 0.5) * magnitude * decay;
      if (k >= 1) {
        group.position.x = baseX;
        group.position.y = baseY;
        return false;
      }
      return true;
    },
  });
};

export const flashScreen = (parent: HTMLElement, color = '#ffffff', durMs = 360): void => {
  const flash = document.createElement('div');
  flash.style.cssText = `
    position: fixed; inset: 0; pointer-events: none; z-index: 999;
    background: ${color}; opacity: 0.85;
    transition: opacity ${durMs}ms cubic-bezier(.2,.6,.4,1);
  `;
  parent.appendChild(flash);
  requestAnimationFrame(() => {
    flash.style.opacity = '0';
  });
  setTimeout(() => flash.remove(), durMs + 50);
};

export const popSolvedBanner = (parent: HTMLElement, text = 'SOLVED!'): void => {
  const el = document.createElement('div');
  el.textContent = text;
  el.style.cssText = `
    position: fixed; inset: 0; display: flex; align-items: center; justify-content: center;
    pointer-events: none; z-index: 998; font-weight: 900;
    font-size: clamp(56px, 14vw, 120px); letter-spacing: -0.04em;
    color: transparent; background: linear-gradient(135deg, #4f46e5 0%, #06b6d4 50%, #f59e0b 100%);
    -webkit-background-clip: text; background-clip: text;
    text-shadow: 0 12px 30px rgba(79, 70, 229, 0.4);
    transform: scale(0.4) rotate(-6deg); opacity: 0;
    transition: transform 0.65s cubic-bezier(.2,1.6,.4,1), opacity 0.4s ease;
  `;
  parent.appendChild(el);
  requestAnimationFrame(() => {
    el.style.transform = 'scale(1) rotate(-2deg)';
    el.style.opacity = '1';
  });
  setTimeout(() => {
    el.style.transition = 'transform 0.4s ease, opacity 0.4s ease';
    el.style.transform = 'scale(1.2) rotate(0deg)';
    el.style.opacity = '0';
  }, 1200);
  setTimeout(() => el.remove(), 1700);
};

void easeOutCubic;
